import {
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PageHeader } from '@/widgets/layout/ui/AppLayout'
import { Button, Field, Input, Select, SkeletonStack, Switch, Textarea } from '@/shared/ui'
import { ApiError } from '@/shared/api/client'
import type {
  Campaign,
  CampaignCreateRequest,
  Contact,
  ContactBulkAction,
  ContactFilter,
  ContactImportResult,
  ContactLabel,
  ContactSelection,
  ContactStatus,
} from '@/shared/api/types'
import { useAccount } from '@/entities/session'
import {
  CAMPAIGN_STATUS,
  CONTACT_LABELS,
  CONTACT_STATUS,
  contactLabel,
  useBulkContacts,
  useContacts,
  useImportContacts,
} from '@/entities/contact'
import { useCampaigns, useCreateCampaign } from '@/entities/campaign'
import { usePromptPreview } from '@/entities/profile'
import { MinutesCard } from '@/entities/minutes'
import { useBots, useBotStore } from '@/entities/bot'
import css from './CampaignsPage.module.css'

const PAGE_SIZE = 25
const FILE_LIMIT_BYTES = 2 * 1024 * 1024
const ALL_LABELS = '__all__'

type PageTab = 'contacts' | 'campaigns'
type LabelFilter = ContactLabel | typeof ALL_LABELS

const STATUS_FILTERS: { value: ContactStatus | ''; label: string }[] = [
  { value: '', label: 'Все' },
  { value: 'new', label: 'Новые' },
  { value: 'no_answer', label: 'Не дозвонились' },
  { value: 'talked', label: 'Поговорили' },
  { value: 'do_not_call', label: 'Не звонить' },
]

export function CampaignsPage() {
  const [tab, setTab] = useState<PageTab>('contacts')

  return (
    <>
      <PageHeader
        title="Обзвоны"
        description="Загрузите базу, выберите нужных клиентов и следите за каждым запуском."
      />

      <MinutesCard />

      <div className={css.tabs} role="tablist" aria-label="Разделы обзвонов">
        <button
          id="contacts-tab"
          type="button"
          role="tab"
          aria-selected={tab === 'contacts'}
          aria-controls="contacts-panel"
          className={`${css.tab} ${tab === 'contacts' ? css.tabActive : ''}`}
          onClick={() => setTab('contacts')}
        >
          База номеров
        </button>
        <button
          id="campaigns-tab"
          type="button"
          role="tab"
          aria-selected={tab === 'campaigns'}
          aria-controls="campaigns-panel"
          className={`${css.tab} ${tab === 'campaigns' ? css.tabActive : ''}`}
          onClick={() => setTab('campaigns')}
        >
          Запуски
        </button>
      </div>

      {tab === 'contacts' ? (
        <ContactsPanel />
      ) : (
        <CampaignList onChooseContacts={() => setTab('contacts')} />
      )}
    </>
  )
}

function ContactsPanel() {
  const navigate = useNavigate()
  const account = useAccount()
  const importContacts = useImportContacts()
  const bulkContacts = useBulkContacts()

  const [status, setStatus] = useState<ContactStatus | ''>('')
  const [label, setLabel] = useState<LabelFilter>(ALL_LABELS)
  const [search, setSearch] = useState('')
  const deferredSearch = useDeferredValue(search)
  const [page, setPage] = useState(0)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set())
  const [allFiltered, setAllFiltered] = useState(false)
  const [bulkError, setBulkError] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)

  const filter = useMemo<ContactFilter>(() => {
    const next: ContactFilter = {}
    if (status) next.status = status
    if (label !== ALL_LABELS) next.label = label
    const query = deferredSearch.trim()
    if (query) next.q = query
    return next
  }, [deferredSearch, label, status])

  const contacts = useContacts({
    filter,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  })

  const items = contacts.data?.items ?? []
  const pageIds = items.map((contact) => contact.id)
  const selectedOnPage = allFiltered
    ? pageIds.length
    : pageIds.filter((id) => selectedIds.has(id)).length
  const wholePageSelected = pageIds.length > 0 && selectedOnPage === pageIds.length
  const pagePartlySelected = selectedOnPage > 0 && !wholePageSelected
  const selectedCount = allFiltered ? (contacts.data?.total ?? 0) : selectedIds.size
  const pageCount = Math.max(1, Math.ceil((contacts.data?.total ?? 0) / PAGE_SIZE))

  const selection = useMemo<ContactSelection>(
    () =>
      allFiltered
        ? { all: true, filter }
        : {
            ids: [...selectedIds],
          },
    [allFiltered, filter, selectedIds],
  )

  function clearSelection() {
    setSelectedIds(new Set())
    setAllFiltered(false)
  }

  function resetForFilter() {
    setPage(0)
    clearSelection()
  }

  function chooseStatus(next: ContactStatus | '') {
    setStatus(next)
    resetForFilter()
  }

  function chooseLabel(next: LabelFilter) {
    setLabel(next)
    resetForFilter()
  }

  function togglePage(checked: boolean) {
    if (allFiltered) {
      clearSelection()
      return
    }

    setSelectedIds((current) => {
      const next = new Set(current)
      for (const id of pageIds) {
        if (checked) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }

  function toggleContact(id: number, checked: boolean) {
    if (allFiltered) {
      const next = new Set(pageIds)
      if (!checked) next.delete(id)
      setSelectedIds(next)
      setAllFiltered(false)
      return
    }

    setSelectedIds((current) => {
      const next = new Set(current)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  async function runBulk(action: ContactBulkAction) {
    if (selectedCount === 0) return

    const messages: Record<ContactBulkAction, string> = {
      do_not_call: `Пометить выбранные номера (${selectedCount}) как «Не звонить»?`,
      restore: `Вернуть выбранные номера (${selectedCount}) в работу?`,
      delete: `Удалить выбранные номера (${selectedCount}) из базы? Это действие нельзя отменить.`,
    }
    if (!window.confirm(messages[action])) return

    setBulkError('')
    try {
      await bulkContacts.mutateAsync({ ...selection, action })
      clearSelection()
      setPage(0)
    } catch (error) {
      setBulkError(errorMessage(error))
    }
  }

  const statusCounts = contacts.data?.counts
  const allStatusCount = statusCounts
    ? statusCounts.new +
      statusCounts.no_answer +
      statusCounts.talked +
      statusCounts.do_not_call
    : 0

  return (
    <section
      id="contacts-panel"
      role="tabpanel"
      aria-labelledby="contacts-tab"
      className={css.stack}
    >
      <ImportPanel
        mutationPending={importContacts.isPending}
        onImport={(text) => importContacts.mutateAsync(text)}
      />

      <section className={css.panel} aria-labelledby="contacts-heading">
        <div className={css.panelHeader}>
          <div>
            <h2 id="contacts-heading" className={css.panelTitle}>
              База номеров
            </h2>
            <p className={css.panelSubtitle}>
              Фильтры выполняются на сервере; выбор сохраняется при переходе между страницами.
            </p>
          </div>
          <Button
            variant="brand"
            size="sm"
            disabled={selectedCount === 0 || bulkContacts.isPending}
            onClick={() => setDialogOpen(true)}
          >
            Новый обзвон
          </Button>
        </div>

        <div className={css.filterBar}>
          <div className={css.statusFilters} aria-label="Статус номера">
            {STATUS_FILTERS.map((option) => {
              const count =
                option.value === ''
                  ? allStatusCount
                  : (statusCounts?.[option.value] ?? 0)
              return (
                <button
                  key={option.value || 'all'}
                  type="button"
                  className={`${css.filterChip} ${status === option.value ? css.filterChipActive : ''}`}
                  aria-pressed={status === option.value}
                  onClick={() => chooseStatus(option.value)}
                >
                  <span>{option.label}</span>
                  <span className={css.filterCount}>{count}</span>
                </button>
              )
            })}
          </div>

          <div className={css.filterControls}>
            <label className={css.compactField}>
              <span className={css.compactLabel}>Метка</span>
              <Select
                aria-label="Фильтр по метке"
                value={label}
                onChange={(event) => chooseLabel(event.target.value as LabelFilter)}
                options={[
                  { value: ALL_LABELS, label: 'Все метки' },
                  ...CONTACT_LABELS.map((option) => ({
                    value: option.value,
                    label: `${option.label} · ${contacts.data?.labelCounts[option.value] ?? 0}`,
                  })),
                ]}
              />
            </label>

            <label className={`${css.compactField} ${css.searchField}`}>
              <span className={css.compactLabel}>Поиск</span>
              <Input
                type="search"
                value={search}
                placeholder="Имя, номер или подпись"
                onChange={(event) => {
                  setSearch(event.target.value)
                  resetForFilter()
                }}
              />
            </label>
          </div>
        </div>

        {selectedCount > 0 && (
          <div className={css.bulkBar} role="region" aria-label="Действия с выбранными номерами">
            <strong>{allFiltered ? `Выбраны все ${selectedCount}` : `Выбрано: ${selectedCount}`}</strong>
            <span className={css.bulkSpacer} />
            <Button
              variant="secondary"
              size="sm"
              loading={bulkContacts.isPending && bulkContacts.variables?.action === 'restore'}
              onClick={() => void runBulk('restore')}
            >
              Вернуть в работу
            </Button>
            <Button
              variant="secondary"
              size="sm"
              loading={bulkContacts.isPending && bulkContacts.variables?.action === 'do_not_call'}
              onClick={() => void runBulk('do_not_call')}
            >
              Не звонить
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={bulkContacts.isPending && bulkContacts.variables?.action === 'delete'}
              onClick={() => void runBulk('delete')}
            >
              Удалить
            </Button>
            <Button variant="ghost" size="sm" disabled={bulkContacts.isPending} onClick={clearSelection}>
              Сбросить
            </Button>
          </div>
        )}

        {bulkError && (
          <div className={css.inlineError} role="alert">
            {bulkError}
          </div>
        )}

        {contacts.isPending ? (
          <div className={css.loadingBlock} aria-label="Загрузка базы номеров">
            <SkeletonStack rows={7} height={42} />
          </div>
        ) : contacts.isError ? (
          <RemoteError
            title="Не удалось загрузить базу номеров"
            message={errorMessage(contacts.error)}
            onRetry={() => void contacts.refetch()}
          />
        ) : items.length === 0 ? (
          <EmptyState
            title={contacts.data?.total ? 'На этой странице ничего нет' : 'Номера не найдены'}
            hint={
              hasFilter(filter)
                ? 'Измените статус, метку или поисковый запрос.'
                : 'Загрузите номера текстом или файлом — они появятся здесь.'
            }
          />
        ) : (
          <>
            <div className={css.tableScroll}>
              <table className={css.table}>
                <thead>
                  <tr>
                    <th className={css.checkColumn} scope="col">
                      <input
                        type="checkbox"
                        aria-label="Выбрать все номера на странице"
                        checked={wholePageSelected}
                        ref={(node) => {
                          if (node) node.indeterminate = pagePartlySelected
                        }}
                        onChange={(event) => togglePage(event.target.checked)}
                      />
                    </th>
                    <th scope="col">Клиент</th>
                    <th scope="col">Подпись</th>
                    <th scope="col">Статус</th>
                    <th scope="col">Метка</th>
                    <th scope="col">Попытки</th>
                    <th scope="col">Последний звонок</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((contact) => (
                    <ContactRow
                      key={contact.id}
                      contact={contact}
                      selected={allFiltered || selectedIds.has(contact.id)}
                      onSelect={(checked) => toggleContact(contact.id, checked)}
                      onOpen={() => navigate(`/clients/${contact.id}`)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {wholePageSelected && (contacts.data?.total ?? 0) > pageIds.length && (
              <div className={css.selectionNotice} role="status">
                {allFiltered ? (
                  <>
                    Выбраны все {contacts.data?.total ?? 0} номеров по текущему фильтру.
                    <button type="button" className={css.textButton} onClick={clearSelection}>
                      Сбросить выбор
                    </button>
                  </>
                ) : (
                  <>
                    Выбраны {pageIds.length} номеров на этой странице.
                    <button
                      type="button"
                      className={css.textButton}
                      onClick={() => {
                        setSelectedIds(new Set())
                        setAllFiltered(true)
                      }}
                    >
                      Выбрать все {contacts.data?.total ?? 0} по фильтру
                    </button>
                  </>
                )}
              </div>
            )}

            <Pagination
              page={page}
              pageCount={pageCount}
              total={contacts.data?.total ?? 0}
              onPage={setPage}
            />
          </>
        )}
      </section>

      {dialogOpen && (
        <NewCampaignDialog
          selection={selection}
          selectedCount={selectedCount}
          defaultCallerNumber={account.data?.company.callerNumber ?? ''}
          onClose={() => setDialogOpen(false)}
          onCreated={(campaign) => {
            clearSelection()
            setDialogOpen(false)
            navigate(`/campaigns/${campaign.id}`)
          }}
        />
      )}
    </section>
  )
}

interface ImportPanelProps {
  mutationPending: boolean
  onImport: (text: string) => Promise<ContactImportResult>
}

function ImportPanel({ mutationPending, onImport }: ImportPanelProps) {
  const fileId = useId()
  const [text, setText] = useState('')
  const [fileName, setFileName] = useState('')
  const [result, setResult] = useState<ContactImportResult | null>(null)
  const [error, setError] = useState('')
  const [invalidOpen, setInvalidOpen] = useState(false)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!text.trim()) {
      setError('Вставьте номера или выберите файл.')
      return
    }

    setError('')
    setInvalidOpen(false)
    try {
      const next = await onImport(text)
      setResult(next)
      setText('')
      setFileName('')
    } catch (reason) {
      setError(errorMessage(reason))
    }
  }

  async function readFile(file: File | undefined) {
    if (!file) return
    setResult(null)
    setError('')

    if (file.size > FILE_LIMIT_BYTES) {
      setFileName('')
      setError('Файл больше 2 МБ. Разделите базу на несколько частей.')
      return
    }

    const lowerName = file.name.toLowerCase()
    if (!lowerName.endsWith('.txt') && !lowerName.endsWith('.csv')) {
      setFileName('')
      setError('Поддерживаются только файлы .txt и .csv.')
      return
    }

    try {
      setText(await file.text())
      setFileName(file.name)
    } catch {
      setFileName('')
      setError('Не удалось прочитать файл в браузере.')
    }
  }

  return (
    <section className={css.importPanel} aria-labelledby="import-heading">
      <div className={css.importIntro}>
        <div>
          <h2 id="import-heading" className={css.panelTitle}>
            Добавить номера
          </h2>
          <p className={css.panelSubtitle}>
            Один номер на строку. После номера можно указать подпись через запятую, точку с
            запятой или табуляцию.
          </p>
        </div>
        <label className={css.fileButton} htmlFor={fileId}>
          <UploadIcon />
          {fileName || 'Выбрать .txt или .csv'}
        </label>
        <input
          id={fileId}
          className={css.fileInput}
          type="file"
          accept=".txt,.csv,text/plain,text/csv"
          onChange={(event) => void readFile(event.target.files?.[0])}
        />
      </div>

      <form className={css.importForm} onSubmit={submit}>
        <label className={css.importLabel} htmlFor={`${fileId}-text`}>
          Номера и подписи
        </label>
        <Textarea
          id={`${fileId}-text`}
          className={css.importTextarea}
          value={text}
          invalid={Boolean(error)}
          aria-describedby={`${fileId}-hint${error ? ` ${fileId}-error` : ''}`}
          placeholder={'79001234567, Анна · закупки\n+7 900 765-43-21; Иван\n8 901 111 22 33'}
          onChange={(event) => {
            setText(event.target.value)
            setResult(null)
            setError('')
          }}
        />
        <div id={`${fileId}-hint`} className={css.importHint}>
          Номера нормализуются к виду 7XXXXXXXXXX. До 20 000 строк и 2 МБ за одну загрузку.
        </div>
        {error && (
          <div id={`${fileId}-error`} className={css.fieldError} role="alert">
            {error}
          </div>
        )}
        <div className={css.importActions}>
          <Button type="submit" loading={mutationPending} disabled={!text.trim()}>
            Добавить в базу
          </Button>
          {fileName && <span className={css.fileMeta}>Прочитан файл: {fileName}</span>}
        </div>
      </form>

      {result && (
        <div className={css.importResult} aria-live="polite">
          <div className={css.summaryGrid}>
            <SummaryNumber label="Добавлено" value={result.added} tone="success" />
            <SummaryNumber label="Уже были" value={result.existing} />
            <SummaryNumber label="Дубли в файле" value={result.duplicates} tone="warning" />
            <SummaryNumber label="Некорректные" value={result.invalid.length} tone="danger" />
          </div>

          {result.invalid.length > 0 && (
            <div className={css.invalidBlock}>
              <button
                type="button"
                className={css.invalidToggle}
                aria-expanded={invalidOpen}
                aria-controls={`${fileId}-invalid-lines`}
                onClick={() => setInvalidOpen((current) => !current)}
              >
                {invalidOpen ? 'Скрыть некорректные строки' : 'Показать некорректные строки'}
                <ChevronIcon open={invalidOpen} />
              </button>
              {invalidOpen && (
                <ol id={`${fileId}-invalid-lines`} className={css.invalidList}>
                  {result.invalid.map((item) => (
                    <li key={`${item.line}-${item.raw}`}>
                      <span>Строка {item.line}</span>
                      <code>{item.raw || 'пустая строка'}</code>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  )
}

function SummaryNumber({
  label,
  value,
  tone = 'neutral',
}: {
  label: string
  value: number
  tone?: 'neutral' | 'success' | 'warning' | 'danger'
}) {
  return (
    <div className={css.summaryItem} data-tone={tone}>
      <strong>{value.toLocaleString('ru-RU')}</strong>
      <span>{label}</span>
    </div>
  )
}

interface ContactRowProps {
  contact: Contact
  selected: boolean
  onSelect: (checked: boolean) => void
  onOpen: () => void
}

function ContactRow({ contact, selected, onSelect, onOpen }: ContactRowProps) {
  const status = CONTACT_STATUS[contact.status]
  const label = CONTACT_LABELS.find((item) => item.value === contact.label)

  function openFromClick(event: ReactMouseEvent<HTMLTableRowElement>) {
    if (isInteractiveTarget(event.target)) return
    onOpen()
  }

  function openFromKeyboard(event: ReactKeyboardEvent<HTMLTableRowElement>) {
    if (event.target !== event.currentTarget || event.key !== 'Enter') return
    event.preventDefault()
    onOpen()
  }

  return (
    <tr
      className={css.tableRow}
      tabIndex={0}
      role="link"
      aria-label={`Открыть клиента ${contact.displayName || contact.phoneFormatted}`}
      onClick={openFromClick}
      onKeyDown={openFromKeyboard}
    >
      <td className={css.checkCell} data-label="Выбрать">
        <input
          type="checkbox"
          checked={selected}
          aria-label={`Выбрать ${contact.displayName || contact.phoneFormatted}`}
          onChange={(event) => onSelect(event.target.checked)}
        />
      </td>
      <td data-label="Клиент">
        <span className={css.personName}>{contact.displayName || contact.phoneFormatted}</span>
        {contact.displayName && <span className={css.personPhone}>{contact.phoneFormatted}</span>}
      </td>
      <td className={css.noteCell} data-label="Подпись" title={contact.note || undefined}>
        {contact.note || '—'}
      </td>
      <td data-label="Статус">
        <span className={css.badge} data-tone={status.tone}>
          {status.label}
        </span>
      </td>
      <td data-label="Метка">
        <span className={css.badge} data-tone={label?.tone ?? 'neutral'}>
          {contactLabel(contact.label)}
        </span>
      </td>
      <td className={css.numberCell} data-label="Попытки">
        {contact.attempts}
      </td>
      <td data-label="Последний звонок">
        {contact.lastCallId ? (
          <Link
            className={css.inlineLink}
            to={`/calls/${contact.lastCallId}`}
            onClick={(event) => event.stopPropagation()}
          >
            {formatShortDate(contact.lastCalledAt)}
          </Link>
        ) : (
          <span className={css.muted}>—</span>
        )}
      </td>
    </tr>
  )
}

function CampaignList({ onChooseContacts }: { onChooseContacts: () => void }) {
  const navigate = useNavigate()
  const campaigns = useCampaigns()

  return (
    <section
      id="campaigns-panel"
      role="tabpanel"
      aria-labelledby="campaigns-tab"
      className={css.panel}
    >
      <div className={css.panelHeader}>
        <div>
          <h2 className={css.panelTitle}>Запуски</h2>
          <p className={css.panelSubtitle}>Активные и завершённые обзвоны в одном списке.</p>
        </div>
        <Button variant="brand" size="sm" onClick={onChooseContacts}>
          Выбрать номера
        </Button>
      </div>

      {campaigns.isPending ? (
        <div className={css.loadingBlock} aria-label="Загрузка запусков">
          <SkeletonStack rows={5} height={64} />
        </div>
      ) : campaigns.isError ? (
        <RemoteError
          title="Не удалось загрузить запуски"
          message={errorMessage(campaigns.error)}
          onRetry={() => void campaigns.refetch()}
        />
      ) : campaigns.data.length === 0 ? (
        <EmptyState
          title="Запусков пока нет"
          hint="Перейдите в базу номеров, выберите клиентов и создайте первый обзвон."
          action={
            <Button variant="brand" size="sm" onClick={onChooseContacts}>
              Перейти к базе
            </Button>
          }
        />
      ) : (
        <div className={css.campaignList}>
          <div className={css.campaignHead} aria-hidden="true">
            <span>Запуск</span>
            <span>Прогресс</span>
            <span>Результаты</span>
            <span>Создан</span>
            <span />
          </div>
          {campaigns.data.map((campaign) => (
            <CampaignRow
              key={campaign.id}
              campaign={campaign}
              onOpen={() => navigate(`/campaigns/${campaign.id}`)}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function CampaignRow({ campaign, onOpen }: { campaign: Campaign; onOpen: () => void }) {
  const complete = campaign.progress.done + campaign.progress.skipped
  const progress = campaign.progress.total
    ? Math.min(100, Math.round((complete / campaign.progress.total) * 100))
    : 0
  const status = CAMPAIGN_STATUS[campaign.status]
  const bots = useBots()
  const botName = campaign.botId ? (bots.data?.names[String(campaign.botId)] ?? 'бот удалён') : ''

  return (
    <button
      type="button"
      className={css.campaignRow}
      aria-label={`Открыть запуск ${campaign.name}`}
      onClick={onOpen}
    >
      <span className={css.campaignIdentity}>
        <span className={css.campaignName}>{campaign.name}</span>
        {botName && (
          <span className={css.badge} data-tone="neutral" title="Бот запуска">
            {botName}
          </span>
        )}
        <span className={css.badge} data-tone={status.tone}>
          {status.label}
        </span>
      </span>
      <span className={css.campaignProgressCell}>
        <span className={css.miniProgress} aria-hidden="true">
          <span style={{ width: `${progress}%` }} />
        </span>
        <span>
          {complete} из {campaign.progress.total} · {progress}%
        </span>
      </span>
      <span className={css.campaignResults}>
        <span>{campaign.progress.talked} поговорили</span>
        <span>{campaign.progress.noAnswer} не ответили</span>
        {campaign.progress.failed > 0 && <span>{campaign.progress.failed} ошибок</span>}
      </span>
      <span className={css.campaignDate}>{formatShortDate(campaign.createdAt)}</span>
      <span className={css.rowArrow} aria-hidden="true">
        →
      </span>
    </button>
  )
}

interface NewCampaignDialogProps {
  selection: ContactSelection
  selectedCount: number
  defaultCallerNumber: string
  onClose: () => void
  onCreated: (campaign: Campaign) => void
}

interface CampaignDraft {
  name: string
  concurrency: string
  ringTimeout: string
  retryAttempts: string
  retryDelayMin: string
  respectHours: boolean
  instructions: string
  greeting: string
  callerNumber: string
  legalConfirmed: boolean
  /** 'main' или id бота строкой: так проще с родным select. */
  bot: string
}

type CampaignFieldErrors = Partial<Record<keyof CampaignDraft | 'selection', string>>

function NewCampaignDialog({
  selection,
  selectedCount,
  defaultCallerNumber,
  onClose,
  onCreated,
}: NewCampaignDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const createCampaign = useCreateCampaign()
  const bots = useBots()
  // По умолчанию — бот, выбранный в сайдбаре: человек только что его
  // правил и тестировал, и запускать, скорее всего, хочет его же.
  const selectedBot = useBotStore((s) => s.botId)
  // Что агент скажет, если первую фразу не трогать: тот же сборщик, что
  // уходит в звонок, а не копия шаблона в браузере. Предпросмотр идёт по
  // боту из сайдбара — подсказка верна, только если в запуске он же.
  const preview = usePromptPreview()
  const [draft, setDraft] = useState<CampaignDraft>({
    name: '',
    concurrency: '1',
    ringTimeout: '40',
    retryAttempts: '0',
    retryDelayMin: '120',
    respectHours: true,
    instructions: '',
    greeting: '',
    callerNumber: defaultCallerNumber,
    legalConfirmed: false,
    bot: selectedBot === null ? 'main' : String(selectedBot),
  })
  const sameAsPreview = draft.bot === (selectedBot === null ? 'main' : String(selectedBot))
  const defaultGreeting = sameAsPreview ? (preview.data?.greeting.trim() ?? '') : ''
  const [errors, setErrors] = useState<CampaignFieldErrors>({})
  const [formError, setFormError] = useState('')

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog && !dialog.open) dialog.showModal()
    return () => {
      if (dialog?.open) dialog.close()
    }
  }, [])

  function update<K extends keyof CampaignDraft>(key: K, value: CampaignDraft[K]) {
    setDraft((current) => ({ ...current, [key]: value }))
    setErrors((current) => ({ ...current, [key]: undefined }))
    setFormError('')
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const parsed = validateCampaign(draft, selectedCount)
    if ('errors' in parsed) {
      setErrors(parsed.errors)
      return
    }

    const request: CampaignCreateRequest = {
      ...parsed.value,
      selection,
      botId: draft.bot === 'main' ? null : Number(draft.bot),
    }

    setErrors({})
    setFormError('')
    try {
      onCreated(await createCampaign.mutateAsync(request))
    } catch (error) {
      if (error instanceof ApiError && error.field) {
        const field = error.field as keyof CampaignFieldErrors
        setErrors({ [field]: error.message })
      } else {
        setFormError(errorMessage(error))
      }
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className={css.dialog}
      aria-labelledby="new-campaign-title"
      onCancel={(event) => {
        event.preventDefault()
        if (!createCampaign.isPending) onClose()
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !createCampaign.isPending) onClose()
      }}
    >
      <form className={css.dialogForm} onSubmit={submit}>
        <div className={css.dialogHeader}>
          <div>
            <h2 id="new-campaign-title">Новый обзвон</h2>
            <p>
              Выбрано {selectedCount.toLocaleString('ru-RU')}{' '}
              {plural(selectedCount, 'номер', 'номера', 'номеров')}
            </p>
          </div>
          <button
            type="button"
            className={css.dialogClose}
            aria-label="Закрыть окно"
            disabled={createCampaign.isPending}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className={css.dialogBody}>
          <Field label="Название запуска" required error={errors.name}>
            {(field) => (
              <Input
                {...field}
                autoFocus
                value={draft.name}
                invalid={Boolean(errors.name)}
                placeholder="Например, Повторный контакт — октябрь"
                onChange={(event) => update('name', event.target.value)}
              />
            )}
          </Field>

          {(bots.data?.items.length ?? 0) > 0 && (
            <Field
              label="Бот"
              hint="Каким ботом звонить. Разные выборки базы можно запустить разными ботами параллельно — минуты общие."
              error={errors.bot}
            >
              {(field) => (
                <Select
                  {...field}
                  value={draft.bot}
                  invalid={Boolean(errors.bot)}
                  options={[
                    { value: 'main', label: 'Основной бот' },
                    ...(bots.data?.items ?? []).map((b) => ({ value: String(b.id), label: b.name })),
                  ]}
                  onChange={(event) => update('bot', event.target.value)}
                />
              )}
            </Field>
          )}

          <div className={css.formGrid}>
            <Field label="Одновременных звонков" required error={errors.concurrency}>
              {(field) => (
                <Select
                  {...field}
                  value={draft.concurrency}
                  invalid={Boolean(errors.concurrency)}
                  options={[
                    { value: '1', label: '1 звонок' },
                    { value: '2', label: '2 звонка' },
                    { value: '3', label: '3 звонка' },
                  ]}
                  onChange={(event) => update('concurrency', event.target.value)}
                />
              )}
            </Field>

            <Field
              label="Ждать ответа, секунд"
              required
              hint="От 15 до 120 секунд"
              error={errors.ringTimeout}
            >
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={15}
                  max={120}
                  step={1}
                  value={draft.ringTimeout}
                  invalid={Boolean(errors.ringTimeout)}
                  onChange={(event) => update('ringTimeout', event.target.value)}
                />
              )}
            </Field>

            <Field label="Повторных попыток" required error={errors.retryAttempts}>
              {(field) => (
                <Select
                  {...field}
                  value={draft.retryAttempts}
                  invalid={Boolean(errors.retryAttempts)}
                  options={[
                    { value: '0', label: 'Не перезванивать' },
                    { value: '1', label: '1 попытка' },
                    { value: '2', label: '2 попытки' },
                    { value: '3', label: '3 попытки' },
                  ]}
                  onChange={(event) => update('retryAttempts', event.target.value)}
                />
              )}
            </Field>

            <Field
              label="Интервал до повтора, минут"
              required
              hint={draft.retryAttempts === '0' ? 'Включится, если добавить повторы' : undefined}
              error={errors.retryDelayMin}
            >
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={1}
                  step={1}
                  value={draft.retryDelayMin}
                  disabled={draft.retryAttempts === '0'}
                  invalid={Boolean(errors.retryDelayMin)}
                  onChange={(event) => update('retryDelayMin', event.target.value)}
                />
              )}
            </Field>
          </div>

          <div className={css.switchBlock}>
            <Switch
              checked={draft.respectHours}
              label="Звонить только в рабочие часы компании"
              onChange={(event) => update('respectHours', event.target.checked)}
            />
            <p>Вне расписания запуск ждёт и продолжает очередь автоматически.</p>
          </div>

          <Field
            label="Сценарий для этого запуска"
            hint="Пусто — агент работает только по основному сценарию компании (Настройка агента → Промпт). Текст отсюда добавляется к нему, а не заменяет."
            error={errors.instructions}
          >
            {(field) => (
              <Textarea
                {...field}
                className={css.dialogTextarea}
                value={draft.instructions}
                invalid={Boolean(errors.instructions)}
                placeholder="Что агенту важно сделать или узнать именно в этом обзвоне"
                onChange={(event) => update('instructions', event.target.value)}
              />
            )}
          </Field>

          <Field
            label="Первая фраза"
            hint={
              (defaultGreeting
                ? `Пусто — агент скажет: «${defaultGreeting}». `
                : 'Пусто — агент скажет первую фразу из настроек выбранного бота. ') +
              'Менять не рекомендуем: приветствие подобрано под голос, и с другим текстом могут поехать интонации.'
            }
            error={errors.greeting}
          >
            {(field) => (
              <Textarea
                {...field}
                className={css.dialogTextarea}
                value={draft.greeting}
                invalid={Boolean(errors.greeting)}
                placeholder="Здравствуйте! Меня зовут…"
                onChange={(event) => update('greeting', event.target.value)}
              />
            )}
          </Field>

          <Field
            label="Номер, с которого звонить"
            hint={
              defaultCallerNumber
                ? `Пусто — звоним с номера компании ${defaultCallerNumber}.`
                : 'Пусто — звоним с общего номера +7 904 953-70-14 (свой номер компании не задан).'
            }
            error={errors.callerNumber}
          >
            {(field) => (
              <Input
                {...field}
                type="tel"
                value={draft.callerNumber}
                invalid={Boolean(errors.callerNumber)}
                placeholder="+7 999 123-45-67"
                onChange={(event) => update('callerNumber', event.target.value)}
              />
            )}
          </Field>

          <label className={`${css.legalCheck} ${errors.legalConfirmed ? css.legalCheckError : ''}`}>
            <input
              type="checkbox"
              checked={draft.legalConfirmed}
              aria-invalid={errors.legalConfirmed ? true : undefined}
              onChange={(event) => update('legalConfirmed', event.target.checked)}
            />
            <span>
              Подтверждаю, что база номеров получена законно и у компании есть основания для
              звонков.
            </span>
          </label>
          {errors.legalConfirmed && (
            <span className={css.fieldError} role="alert">
              {errors.legalConfirmed}
            </span>
          )}
          {errors.selection && (
            <span className={css.fieldError} role="alert">
              {errors.selection}
            </span>
          )}
          {formError && (
            <div className={css.inlineError} role="alert">
              {formError}
            </div>
          )}
        </div>

        <div className={css.dialogFooter}>
          <Button variant="ghost" disabled={createCampaign.isPending} onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" variant="brand" loading={createCampaign.isPending}>
            Запустить обзвон
          </Button>
        </div>
      </form>
    </dialog>
  )
}

function validateCampaign(
  draft: CampaignDraft,
  selectedCount: number,
): { value: Omit<CampaignCreateRequest, 'selection' | 'botId'> } | { errors: CampaignFieldErrors } {
  const errors: CampaignFieldErrors = {}
  const concurrency = integerInRange(draft.concurrency, 1, 3)
  const ringTimeout = integerInRange(draft.ringTimeout, 15, 120)
  const retryAttempts = integerInRange(draft.retryAttempts, 0, 3)
  const retryDelayMin = positiveInteger(draft.retryDelayMin)

  if (!draft.name.trim()) errors.name = 'Введите название запуска.'
  if (concurrency === null) errors.concurrency = 'Выберите от 1 до 3 звонков.'
  if (ringTimeout === null) errors.ringTimeout = 'Укажите целое число от 15 до 120.'
  if (retryAttempts === null) errors.retryAttempts = 'Выберите от 0 до 3 повторов.'
  if (retryDelayMin === null) errors.retryDelayMin = 'Укажите целое число минут больше нуля.'
  if (!draft.legalConfirmed) {
    errors.legalConfirmed = 'Без подтверждения законности базы запуск невозможен.'
  }
  if (selectedCount === 0) errors.selection = 'Выберите хотя бы один номер.'

  if (
    Object.keys(errors).length > 0 ||
    concurrency === null ||
    ringTimeout === null ||
    retryAttempts === null ||
    retryDelayMin === null
  ) {
    return { errors }
  }

  return {
    value: {
      name: draft.name.trim(),
      concurrency,
      ringTimeout,
      retryAttempts,
      retryDelayMin,
      respectHours: draft.respectHours,
      instructions: draft.instructions.trim(),
      greeting: draft.greeting.trim(),
      callerNumber: draft.callerNumber.trim(),
      legalConfirmed: draft.legalConfirmed,
    },
  }
}

function Pagination({
  page,
  pageCount,
  total,
  onPage,
}: {
  page: number
  pageCount: number
  total: number
  onPage: (page: number) => void
}) {
  if (total === 0) return null

  return (
    <nav className={css.pagination} aria-label="Страницы базы номеров">
      <span>
        {total.toLocaleString('ru-RU')} {plural(total, 'номер', 'номера', 'номеров')}
      </span>
      <div>
        <Button
          variant="secondary"
          size="sm"
          disabled={page === 0}
          onClick={() => onPage(Math.max(0, page - 1))}
        >
          Назад
        </Button>
        <span className={css.pageNumber} aria-live="polite">
          {page + 1} из {pageCount}
        </span>
        <Button
          variant="secondary"
          size="sm"
          disabled={page + 1 >= pageCount}
          onClick={() => onPage(Math.min(pageCount - 1, page + 1))}
        >
          Далее
        </Button>
      </div>
    </nav>
  )
}

function RemoteError({
  title,
  message,
  onRetry,
}: {
  title: string
  message: string
  onRetry: () => void
}) {
  return (
    <div className={css.remoteState} role="alert">
      <span className={css.remoteIcon}>!</span>
      <strong>{title}</strong>
      <p>{message}</p>
      <Button variant="secondary" size="sm" onClick={onRetry}>
        Повторить
      </Button>
    </div>
  )
}

function EmptyState({
  title,
  hint,
  action,
}: {
  title: string
  hint: string
  action?: React.ReactNode
}) {
  return (
    <div className={css.remoteState}>
      <span className={css.emptyIcon} aria-hidden="true">
        <PhoneBookIcon />
      </span>
      <strong>{title}</strong>
      <p>{hint}</p>
      {action}
    </div>
  )
}

function integerInRange(value: string, min: number, max: number): number | null {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed >= min && parsed <= max ? parsed : null
}

function positiveInteger(value: string): number | null {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

function hasFilter(filter: ContactFilter): boolean {
  return Boolean(filter.status || filter.label !== undefined || filter.q)
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  return 'Что-то пошло не так. Попробуйте ещё раз.'
}

function formatShortDate(value: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  })
}

function plural(value: number, one: string, few: string, many: string): string {
  const mod10 = value % 10
  const mod100 = value % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
  return many
}

function isInteractiveTarget(target: EventTarget): boolean {
  return target instanceof Element && Boolean(target.closest('a, button, input, select, textarea, label'))
}

function UploadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 10.5V2.5M5 5.5L8 2.5L11 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.5 10V12.5C2.5 13.1 2.9 13.5 3.5 13.5H12.5C13.1 13.5 13.5 13.1 13.5 12.5V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="12"
      height="8"
      viewBox="0 0 12 8"
      fill="none"
      aria-hidden="true"
      className={open ? css.chevronOpen : undefined}
    >
      <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function PhoneBookIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <rect x="5" y="4" width="18" height="20" rx="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M9 2.5V6M14 2.5V6M19 2.5V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M10.5 12.5H17.5M10.5 17H15.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}
